import { useEffect, useRef, useState } from "preact/hooks";

import { FunctionComponent } from "preact";
import { NULL, FALSE, TRUE } from "constants/primitives";
import integrationSettings from "core/integrationSettings";
import useAppState from "hooks/useAppState";
import useTranslation from "hooks/useTranslation";
import CookieFirstTCFApi from "./CookieFirstTCFApi";
import useLoadTCFGlobalVendorList from "hooks/useLoadTCFGlobalVendorList";
import useTCFUserConsent from "hooks/useTCFUserConsent";
import useLoadGoogleVendorList from "hooks/useLoadGoogleVendorList";
import { getTcData, ITcDataReturn } from "helpers/tcf/getTcData";
import { NUMBER_0, NUMBER_1 } from "constants/numbers";

const TCFApi: FunctionComponent<ITCFApiProps> = () => {
  const {
    widgetConfig,
    setGvl,
    setGatp,
    isTCFPublicEndpointInit,
    setIsTCFPublicEndpointInit,
  } = useAppState();
  const consent = useTCFUserConsent();
  const { userLang } = useTranslation();
  const { stealthMode } = integrationSettings;

  // API instance is assigned to ref in order to prevent memory leak
  const apiRef = useRef<CookieFirstTCFApi | null>(NULL);
  const [gvl, isGvlLoading] = useLoadTCFGlobalVendorList();
  const [gatp, isGatpLoading] = useLoadGoogleVendorList();
  const [tcfCallbackFunctions, setTcfCallbackFunctions] =
    useState<TCFCallbackFunctions>({});

  useEffect(() => {
    // Init TCF API but wait for Global Vendor List to load
    if (isGvlLoading || (widgetConfig.acEnabled ? isGatpLoading : FALSE)) {
      return;
    }

    if (gvl) {
      setGvl(gvl);
    }

    if (gatp && widgetConfig.acEnabled) {
      setGatp(gatp);
    }

    // Save gvl to consent state
    apiRef.current = new CookieFirstTCFApi({
      userLang,
      consent,
      widgetConfig,
      stealthMode,
      gvl,
      callbackFunctions: tcfCallbackFunctions,
      setCallbackFunctions: setTcfCallbackFunctions,
      isPublicEndpointInit: isTCFPublicEndpointInit,
      setPublicEndpointInitialized: setIsTCFPublicEndpointInit,
    });
  }, [
    tcfCallbackFunctions,
    setTcfCallbackFunctions,
    userLang,
    widgetConfig,
    stealthMode,
    isGvlLoading,
    isGatpLoading,
    consent,
    gvl,
    setGvl,
    gatp,
    setGatp,
    isTCFPublicEndpointInit,
    setIsTCFPublicEndpointInit,
  ]);

  useEffect(() => {
    if (consent.triggerTCFConsentUpdate) {
      Object.entries(tcfCallbackFunctions).forEach((item) => {
        getTcData(item[NUMBER_0], consent, widgetConfig, gvl, userLang).then(
          (callBackTCData) => {
            item[NUMBER_1](callBackTCData, TRUE);
          }
        );
      });
    }
  }, [
    tcfCallbackFunctions,
    consent.triggerTCFConsentUpdate,
    gvl,
    consent,
    userLang,
    widgetConfig,
  ]);

  return NULL;
};

export interface ITCFApiProps {
  placeholder?: never;
}

export type TCFCallbackFunctions = {
  [key: string]: (callBackTCData: ITcDataReturn, success: boolean) => void;
};

export default TCFApi;
